import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import UserCourseDetailDialog from '../components/UserCourseDetailDialog';

interface CompanyAnalytics {
  companyId: string;
  companyName: string;
  companyEmail: string;
  createdAt: string;
  isActive: boolean;
  metrics: {
    totalUsers: number;
    usersEnrolledInCourses: number;
    usersCompletedCourses: number;
    totalCoursesEnrolled: number;
    totalCoursesCompleted: number;
    averageScore: number;
    totalScore: number;
    completionPercentage: number;
  };
  userDetails: Array<{
    userId: string;
    name: string;
    email: string;
    role: string;
    enrolledCourses: number;
    completedCourses: number;
    totalScore: number;
    averageScore: number;
  }>;
}

interface AnalyticsSummary {
  totalCompanies: number;
  totalUsers: number;
  totalEnrolledUsers: number;
  totalCompletedUsers: number;
  overallAverageScore: number;
}

const AdminCompanyAnalytics: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyAnalytics[]>([]);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyAnalytics | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [userCourseDetailDialogOpen, setUserCourseDetailDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId: string; userName: string; userEmail: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [sortColumn, setSortColumn] = useState<string>('completionPercentage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (user?.role === 'admin') {
      loadCompanyAnalytics();
    }
  }, [user, statusFilter]);

  const loadCompanyAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getCompanyAnalytics(statusFilter);
      console.log('📊 Datos recibidos del backend:', data);
      setCompanies(data.companies);
      setSummary(data.summary);
    } catch (err: any) {
      console.error('Error cargando analíticas:', err);
      setError(err.response?.data?.message || 'Error cargando analíticas de empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (company: CompanyAnalytics) => {
    console.log('🔍 Abriendo detalles de empresa:', company);
    console.log('👥 Detalles de usuarios:', company.userDetails);
    setSelectedCompany(company);
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleViewUserCourses = (userId: string, userName: string, userEmail: string) => {
    setSelectedUser({ userId, userName, userEmail });
    setUserCourseDetailDialogOpen(true);
  };

  const handleCloseUserCourseDetails = () => {
    setUserCourseDetailDialogOpen(false);
    setSelectedUser(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getScoreColor = (score: number) => {
    if (score >= 4) return 'success';  // 4 o más puntos (excelente)
    if (score >= 3) return 'warning';  // 3-3.9 puntos (bueno)
    return 'error';                    // Menos de 3 puntos (necesita mejorar)
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Si es la misma columna, cambiar dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nueva columna, ordenar descendente por defecto
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedCompanies = useMemo(() => {
    const sorted = [...companies];
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'companyName':
          aValue = a.companyName.toLowerCase();
          bValue = b.companyName.toLowerCase();
          break;
        case 'isActive':
          aValue = a.isActive ? 1 : 0;
          bValue = b.isActive ? 1 : 0;
          break;
        case 'totalUsers':
          aValue = a.metrics.totalUsers;
          bValue = b.metrics.totalUsers;
          break;
        case 'usersEnrolledInCourses':
          aValue = a.metrics.usersEnrolledInCourses;
          bValue = b.metrics.usersEnrolledInCourses;
          break;
        case 'usersCompletedCourses':
          aValue = a.metrics.usersCompletedCourses;
          bValue = b.metrics.usersCompletedCourses;
          break;
        case 'completionPercentage':
          aValue = a.metrics.completionPercentage;
          bValue = b.metrics.completionPercentage;
          break;
        case 'averageScore':
          aValue = a.metrics.averageScore;
          bValue = b.metrics.averageScore;
          break;
        case 'totalScore':
          aValue = a.metrics.totalScore;
          bValue = b.metrics.totalScore;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [companies, sortColumn, sortDirection]);

  const SortableTableHeader: React.FC<{ 
    column: string; 
    label: string; 
    align?: 'left' | 'center' | 'right' 
  }> = ({ column, label, align = 'center' }) => {
    const isActive = sortColumn === column;
    return (
      <TableCell 
        align={align}
        sx={{ 
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
        onClick={() => handleSort(column)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', gap: 0.5 }}>
          <strong>{label}</strong>
          {isActive && (
            sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
          )}
        </Box>
      </TableCell>
    );
  };

  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No tienes permisos para acceder a esta sección.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando analíticas...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadCompanyAnalytics}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon color="primary" />
          Analíticas de Empresas
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Resumen del rendimiento y progreso de todas las empresas en la plataforma
        </Typography>
        
        {/* Filtro de Estado */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Estado de Empresa</InputLabel>
            <Select
              value={statusFilter}
              label="Estado de Empresa"
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            >
              <MenuItem value="active">Empresas Activas</MenuItem>
              <MenuItem value="inactive">Empresas Inactivas</MenuItem>
              <MenuItem value="all">Todas las Empresas</MenuItem>
            </Select>
          </FormControl>
          <Chip 
            label={`${companies.length} empresas`} 
            color="primary" 
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      {/* Resumen General */}
      {summary && (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3, 
          mb: 4 
        }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="primary">
                    {summary.totalCompanies}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Empresas Registradas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {summary.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total de Usuarios
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {summary.totalEnrolledUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Usuarios Inscritos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {summary.totalCompletedUsers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Usuarios Completados
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tabla de Empresas */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon />
            Detalle por Empresa
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableHeader column="companyName" label="Empresa" align="left" />
                  <SortableTableHeader column="totalUsers" label="Total Usuarios" />
                  <SortableTableHeader column="usersEnrolledInCourses" label="Inscritos en Cursos" />
                  <SortableTableHeader column="usersCompletedCourses" label="Completaron Cursos" />
                  <SortableTableHeader column="completionPercentage" label="% Completitud" />
                  <SortableTableHeader column="averageScore" label="Puntaje Promedio" align="right" />
                  <SortableTableHeader column="totalScore" label="Suma de Puntajes" align="right" />
                  <SortableTableHeader column="createdAt" label="Fecha Registro" />
                  <SortableTableHeader column="isActive" label="Estado" />
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCompanies.map((company) => (
                  <TableRow key={company.companyId} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {company.companyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {company.companyEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={company.metrics.totalUsers} 
                        color="info" 
                        variant="outlined"
                        icon={<PeopleIcon />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={company.metrics.usersEnrolledInCourses} 
                        color="warning" 
                        variant="outlined"
                        icon={<SchoolIcon />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={company.metrics.usersCompletedCourses} 
                        color="success" 
                        variant="outlined"
                        icon={<CheckCircleIcon />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${company.metrics.completionPercentage.toFixed(1)}%`} 
                        color={
                          company.metrics.completionPercentage >= 80 ? 'success' :
                          company.metrics.completionPercentage >= 50 ? 'warning' : 'error'
                        }
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {company.metrics.averageScore}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {company.metrics.totalScore.toFixed(1)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {formatDate(company.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={company.isActive ? 'Activa' : 'Inactiva'} 
                        color={company.isActive ? 'success' : 'error'}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetails(company)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Diálogo de Detalles */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detalles de {selectedCompany?.companyName}
          </Typography>
          <IconButton onClick={handleCloseDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedCompany && (
            <Box>
              {/* Métricas de la empresa */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2, 
                mb: 3 
              }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="primary">
                      {selectedCompany.metrics.totalUsers}
                    </Typography>
                    <Typography variant="caption">Total Usuarios</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="warning.main">
                      {selectedCompany.metrics.usersEnrolledInCourses}
                    </Typography>
                    <Typography variant="caption">Inscritos</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="success.main">
                      {selectedCompany.metrics.usersCompletedCourses}
                    </Typography>
                    <Typography variant="caption">Completados</Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6" color="info.main">
                      {selectedCompany.metrics.averageScore}
                    </Typography>
                    <Typography variant="caption">Promedio</Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Tabla de usuarios */}
              <Typography variant="h6" gutterBottom>
                Detalle por Usuario
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Usuario</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell align="center"><strong>Rol</strong></TableCell>
                      <TableCell align="center"><strong>Cursos Inscritos</strong></TableCell>
                      <TableCell align="center"><strong>Cursos Completados</strong></TableCell>
                      <TableCell align="center"><strong>Puntaje Total</strong></TableCell>
                      <TableCell align="center"><strong>Puntaje Promedio</strong></TableCell>
                      <TableCell align="center"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCompany.userDetails.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {user.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={user.role} 
                            size="small" 
                            color={user.role === 'admin' ? 'error' : user.role === 'company_admin' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {user.enrolledCourses}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {user.completedCourses}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {user.totalScore || 0} pts
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${user.averageScore}`} 
                            size="small"
                            color={getScoreColor(user.averageScore) as any}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewUserCourses(user.userId, user.name, user.email)}
                            disabled={user.enrolledCourses === 0}
                          >
                            Ver Cursos
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de detalles de cursos del usuario */}
      {selectedUser && (
        <UserCourseDetailDialog
          open={userCourseDetailDialogOpen}
          onClose={handleCloseUserCourseDetails}
          userId={selectedUser.userId}
          userName={selectedUser.userName}
          userEmail={selectedUser.userEmail}
        />
      )}
    </Box>
  );
};

export default AdminCompanyAnalytics;
